import React, { useEffect, useState, useRef } from 'react';
import { Button } from '@3plearning/react-components';
import { t } from '@lingui/macro';
import propTypes from 'prop-types';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import PortalCommunication from '../../helpers/portal-comms';
import ShowFilter from './show-filter';
import { getLocale } from '../../helpers/locale';
import {
  MoreIcon,
  Visible,
  CollapseAllIcon,
  Expand,
  ExcelExportIcon,
  Info,
  FilterOutlineIcon
} from '../../helpers/icon-dictionary';
import OutsideClickHandler from '../../helpers/hoc/close/outside-click-handler';
import ActionsList from '../actions-list';
import Legend from '../legend';
import css from './filter-bar.scss';
import {
  TOGGLE_TOPICS_EXPAND_COLLAPSE,
  UPDATE_DATE_PREFERENCE
} from '../../containers/course-assign/course-assign-types';
import { getConfiguration } from '../../helpers/utils';
import { getDatePreference } from '../../helpers/resources-helper';
import StudentAccess from '../student-access/student-access';
import { useTracking } from '../../analytics';

// Only import download-excel if not Internet Explorer,
// as this module breaks the whole application on IE11
const isIE =
  navigator.userAgent.indexOf('MSIE') !== -1 ||
  navigator.appVersion.indexOf('Trident/') > -1;
const DownloadExcel = isIE
  ? undefined
  : require('../../helpers/exportexcel/download-excel').default;

function FilterBar({ i18n }) {
  const dispatch = useDispatch();
  const { trackHowItWorks, trackViewKey, trackMenuAction } = useTracking();
  const [isLegendVisible, setLegendVisible] = useState(false);

  const [showActionsList, setActionsList] = useState(false);
  const [showFilter, setShowFilter] = useState(false);

  const [isDownloadInProgress, setDownloadProgressFlag] = useState(false);

  const gridTopics = useSelector(
    (state) => state.CourseAssignReducer.gridTopics,
    shallowEqual
  );

  const assignments = useSelector(
    (state) => state.CourseAssignReducer.assignments,
    shallowEqual
  );

  const classrooms = useSelector(
    (state) => state.CourseAssignReducer.classrooms
  );

  const activeClass = useSelector(
    (state) => state.CourseAssignReducer.activeClass
  );
  const activeGroup = useSelector(
    (state) => state.CourseAssignReducer.activeGroup
  );
  const filteredStudents = useSelector(
    (state) => state.CourseAssignReducer.filteredStudents
  );

  const girlIds = useRef([]);

  function hideLegend() {
    setLegendVisible(false);
  }

  function hideFilter() {
    setShowFilter(false);
  }

  const exportExcel = () => {
    if (isIE) {
      return;
    }
    setDownloadProgressFlag(true);
    const activeClassDetails = classrooms.find(
      (currentClass) => currentClass.classroomId === activeClass
    );
    const activeClassName = activeClassDetails ? activeClassDetails.name : '';
    const activeGroupDetails = activeClassDetails.groups.find(
      (groupDetails) => groupDetails.groupId === activeGroup
    );
    const activeGroupName = activeGroupDetails ? activeGroupDetails.name : '';
    DownloadExcel.processDataForExcel(
      assignments,
      i18n,
      gridTopics,
      filteredStudents,
      activeClassName,
      activeGroupName,
      setDownloadProgressFlag
    );
  };
  /* eslint-disable indent */
  // Only show option if not internet explorer
  // Internet Explorer will break if the exceljs module is imported
  const exportExcelItem = isIE
    ? []
    : [
        {
          icon: ExcelExportIcon,
          text: i18n._(t('courseAssign.actions.exportExcel')`Export to Excel`),
          onClick: () => {
            trackMenuAction({ action: 'export-excel' });
            exportExcel();
            setActionsList(false);
          },
          disabled: isDownloadInProgress
        }
      ];
  /* eslint-enable indent */

  const actionsListOptions = [
    ...exportExcelItem,
    {
      icon: Expand,
      text: i18n._(t('courseAssign.actions.expandAll')`Expand all`),
      onClick: () => {
        trackMenuAction({ action: 'expand-all' });
        dispatch({
          type: TOGGLE_TOPICS_EXPAND_COLLAPSE,
          setTopicsCollapsed: false
        });
        setActionsList(false);
      }
    },
    {
      icon: CollapseAllIcon,
      text: i18n._(t('courseAssign.actions.collapseAll')`Collapse all`),
      onClick: () => {
        trackMenuAction({ action: 'collapse-all' });
        dispatch({
          type: TOGGLE_TOPICS_EXPAND_COLLAPSE,
          setTopicsCollapsed: true
        });
        setActionsList(false);
      }
    }
  ];
  useEffect(() => {
    getConfiguration('girlIdsForInfoModal').then(
      (value) => (girlIds.current = JSON.parse(value) || [])
    );
    getDatePreference().then((datePreference) => {
      dispatch({
        type: UPDATE_DATE_PREFERENCE,
        payload: datePreference
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const invokeInfoModal = async () => {
    trackHowItWorks();
    PortalCommunication.requestInfoModal(girlIds.current);
  };

  const showHowThisWorks = () => {
    const locale = getLocale();
    if (locale && locale.toLowerCase() === 'es-mx') {
      return null;
    }
    return (
      <Button
        alignIcon="LEFT"
        className={css.howThisWorks}
        onClick={invokeInfoModal}
        icon={Info}
        text={i18n._(t('courseAssign.button.hdtw')`How does this work?`)}
        primary
        type={Button.LOZENGE}
      />
    );
  };

  return (
    <div className={css.filterBar}>
      <span className={css.actions} data-testid="show-filter-button">
        <OutsideClickHandler
          callback={() => setShowFilter(false)}
          forButton="showFilter"
        >
          <Button
            className={`${css.showFilter} ${css.keyActive} ${
              showFilter && css.actionsActive
            }`}
            icon={FilterOutlineIcon}
            onClick={() => setShowFilter(!showFilter)}
            primary
            text={i18n._(
              t('courseAssign.button.topicSelector')`Topic Selector`
            )}
            type={Button.LOZENGE}
          />

          <div id="showFilter">
            <ShowFilter
              isOpen={showFilter}
              i18n={i18n}
              onApply={() => hideFilter()}
            />
          </div>
        </OutsideClickHandler>
      </span>

      <div className={css.buttonsContainer}>
        {girlIds.current.length === 0 ? null : showHowThisWorks()}
        <div className={css.bottomButtons}>
          <span
            id="legendButton"
            onClick={() => {}}
            role="button"
            tabIndex="0"
            className={css.viewKeyContainer}
          >
            <Button
              alignIcon="LEFT"
              className={css.key}
              icon={Visible}
              onClick={() => {
                trackViewKey();
                setLegendVisible(!isLegendVisible);
              }}
              primary
              text={i18n._(t('courseAssign.button.viewKey')`View Key`)}
              type={Button.LOZENGE}
            />
            <OutsideClickHandler
              callback={() => hideLegend()}
              forButton="legendButton"
            >
              <Legend
                i18n={i18n}
                isVisible={isLegendVisible}
                css={css}
                onApply={() => hideLegend()}
              />
            </OutsideClickHandler>
          </span>
          <OutsideClickHandler
            callback={() => setActionsList(false)}
            forButton="actionsButton"
          >
            <span className={css.actions}>
              <Button
                alignIcon="LEFT"
                className={`${css.key} ${css.keyActive} ${
                  showActionsList && css.actionsActive
                }`}
                icon={MoreIcon}
                onClick={() => setActionsList(!showActionsList)}
                primary
                text={i18n._(t('courseAssign.button.actions')`Actions`)}
                type={Button.LOZENGE}
              />
              <ActionsList
                options={actionsListOptions}
                isOpen={showActionsList}
              />
            </span>
          </OutsideClickHandler>
          <div className={css.toggleWrapper}>
            <StudentAccess i18n={i18n} />
          </div>
        </div>
      </div>
    </div>
  );
}
FilterBar.propTypes = {
  i18n: propTypes.object.isRequired
};

export default FilterBar;



// WEBPACK FOOTER //
// ./components/filter-bar/index.js